<template>
  <div class="block">
    <div class="sub-title">游客{{ index + 1 }}</div>

    <!-- 免票弹窗：当计算得出 ticketType 为 '免票' 时自动弹出 -->
    <el-dialog
      :visible.sync="showFreeTicketDialog"
      title="免票提示"
      width="420px"
    >
      <div>当前为免票，无需当前信息。</div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="acknowledgeFreeTicket">我知道了</el-button>
      </span>
    </el-dialog>

    <el-form :model="value" label-width="120px" class="form">
      <!-- 如果计算出的票种为免票，则提示用户无需录入证件/手机号等信息 -->
      <el-row :gutter="16" v-if="String(value.ticketType) === '免票'">
        <el-col :span="24">
          <el-alert
            title="免票：该游客为免票，无需录入信息。"
            type="info"
            show-icon
            :closable="false"
          />
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :span="8">
          <el-form-item label="游客姓名" required>
            <el-input
              :value="value.name"
              placeholder="请输入游客姓名"
              @input="updateField('name', $event)"
            />
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="证件号码" required>
            <el-input
              :value="value.idNo"
              placeholder="请输入身份证号或其他证件号"
              @input="onIdNoInput"
            />
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="手机号码" required>
            <el-input
              :value="value.phone"
              placeholder="请输入手机号码"
              @input="updateField('phone', $event)"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :span="8">
          <el-form-item label="游客年龄" required>
            <el-input
              :value="value.age"
              :placeholder="isMainlandId ? '系统将自动计算' : '请输入年龄'"
              :disabled="isMainlandId"
              @input="onAgeInput"
            />
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="观礼平台" required>
            <el-select
              :value="value.platform"
              placeholder="请选择观礼平台"
              style="width: 100%"
              @input="updateField('platform', $event)"
            >
              <el-option v-for="opt in platformOptions" :key="opt" :label="opt" :value="opt" />
            </el-select>
          </el-form-item>
        </el-col>

        <!-- 票种类型显示已移除，统计由下方票种统计区显示 -->

        <el-col :span="8">
          <el-form-item label="游玩区域" required>
            <el-select
              :value="value.area"
              placeholder="请选择游玩区域"
              style="width: 100%"
              @input="updateField('area', $event)"
            >
              <el-option v-for="opt in areaOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="16" style="margin-top:6px">
        <el-col :span="24">
          <el-form-item label="备注" label-width="80px">
            <el-input
              type="textarea"
              :value="value.remark"
              placeholder="可选：填写备注信息（现场说明/特殊需求等），将同步到核销备注"
              :rows="2"
              @input="updateField('remark', $event)"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <div class="block-actions">
        <el-button
          v-if="showRemove"
          type="danger"
          plain
          icon="el-icon-delete"
          size="mini"
          @click="$emit('remove', index)"
        >
          删除该游客
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { calcAge, parseBirthDateFromIdCard } from '../utils/idCard'

export default {
  name: 'VisitorInfoBlock',
  props: {
    value: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    showRemove: {
      type: Boolean,
      default: false
    }
    ,
    platformOptions: {
      type: Array,
      default: () => []
    },
    areaOptions: {
      type: Array,
      default: () => []
    },
    ticketTypeOptions: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    isMainlandId() {
      // 能解析出生日期，认为是中国大陆二代身份证（18位）
      return !!parseBirthDateFromIdCard(this.value && this.value.idNo)
    }
  },
  data() {
    return {
      showFreeTicketDialog: false
    }
  },
  watch: {
    // watcher removed: 弹窗由计算逻辑直接触发（见 computeTicketTypeAndMaybePrompt）
  },
  methods: {
    acknowledgeFreeTicket() {
      // 清空当前游客的录入信息（包括票种），并关闭弹窗
      const cleared = {
        ...this.value,
        name: '',
        idNo: '',
        phone: '',
        age: '',
        platform: '',
        area: '',
        remark: '',
        ticketType: ''
      }
      this.$emit('input', cleared)
      this.showFreeTicketDialog = false
    },
    getPlatformPolicy(platform, area) {
      // 返回平台的年龄政策：{ minAge, childMin, childMax, adultMin, specialAreaAge (optional) }
      // 说明：当未定义 child 区间时，默认将符合年龄要求者视为成人票
      const p = String(platform || '').trim()
      switch (p) {
        case '天航平台':
          return { minAge: 4, childMin: 4, childMax: 13, adultMin: 14 }
        case '瑶光平台':
          return { minAge: 5, adultMin: 5 }
        case '梦舟平台':
          return { minAge: 5, childMin: 5, childMax: 12, adultMin: 13 }
        case '揽月平台':
          // 高台区要求 18 周岁及以上
          if (String(area) === '高台区') {
            return { minAge: 18, adultMin: 18, specialArea: '高台区' }
          }
          return { minAge: 5, childMin: 5, childMax: 12, adultMin: 13 }
        case '航天神舟':
          return { minAge: 3, adultMin: 3 }
        default:
          return { minAge: 0, adultMin: 0 }
      }
    },

    computeTicketTypeAndMaybePrompt(platform, area, age) {
      // age can be number or empty string; treat missing as 0 per需求
      const ageNum = (typeof age === 'number' && !Number.isNaN(age)) ? age : (parseInt(String(age || '0'), 10) || 0)
      const policy = this.getPlatformPolicy(platform, area)

      // prepare base object using current value but override platform/area/age from args
      const base = { ...this.value, platform: platform || this.value.platform, area: area || this.value.area, age: ageNum }

      // if platform not selected, clear ticketType and emit
      if (!platform) {
        this.$emit('input', { ...base, ticketType: '' })
        return
      }

      // check special minimum (exempt) case
      if (policy.minAge && ageNum < policy.minAge) {
        // 不再使用票种 '免票'；改为弹窗提示并保持票种为空（上层统计不计入免票）
        // 打开弹窗并清空/标记当前游客为无需录入信息
        this.showFreeTicketDialog = true
        this.$emit('input', { ...base, ticketType: '', age: ageNum })
        return
      }

      // Special-case: certain platforms (瑶光平台、航天神舟) use "通用票"
      // for everyone >= adultMin (5+) and are exempt (< minAge) handled above.
      const normalizedPlatform = String(platform || '').trim()
      if (normalizedPlatform === '瑶光平台' || normalizedPlatform === '航天神舟') {
        let ticketForPlatform = ''
        if (ageNum >= (policy.adultMin || 0)) {
          ticketForPlatform = '通用票'
        } else {
          ticketForPlatform = ''
        }
        this.$emit('input', { ...base, ticketType: ticketForPlatform, age: ageNum })
        return
      }

      // 根据 child 区间判断儿童票，否则成人票（若未配置 child 区间，则默认成人票）
      let ticket = ''
      if (policy.childMin !== undefined && policy.childMax !== undefined) {
        if (ageNum >= policy.childMin && ageNum <= policy.childMax) {
          ticket = '儿童票'
        } else if (ageNum >= (policy.adultMin || 0)) {
          ticket = '成人票'
        } else {
          ticket = '' // fallback
        }
      } else {
        // 没有儿童区间，符合 adultMin 的一律视为成人（例如瑶光/航天神舟）
        if (ageNum >= (policy.adultMin || 0)) {
          ticket = '成人票'
        } else {
          ticket = ''
        }
      }

      this.$emit('input', { ...base, ticketType: ticket, age: ageNum })
    },

    updateField(field, val) {
      // 如果更改 platform，且当前 area 不在新平台的可选项中，则清空 area；同时清空 ticketType
      if (field === 'platform') {
        const areaValues = (this.areaOptions || []).map(x => x.value)
        const next = { ...this.value, platform: val, ticketType: '' }
        if (this.value && this.value.area && !areaValues.includes(this.value.area)) {
          next.area = ''
        }
        // 先更新 platform，再计算票种（age 可能已存在或为空）
        this.$emit('input', next)
        // compute ticketType after a short delay to allow parent to update bindings
        this.$nextTick(() => {
          this.computeTicketTypeAndMaybePrompt(val, next.area, next.age)
        })
        return
      }

      // 如果更改 area，需要重新基于当前年龄和新 area 计算票种（例如揽月平台高台区需 18+）
      if (field === 'area') {
        const next = { ...this.value, area: val }
        this.$emit('input', next)
        this.$nextTick(() => {
          // use updated platform (可能为空)、新的 area 值，以及当前 age 值（若为空则按 0 处理）
          this.computeTicketTypeAndMaybePrompt(next.platform, val, next.age)
        })
        return
      }
      this.$emit('input', { ...this.value, [field]: val })
    },
    onAgeInput(val) {
      // 仅在非大陆身份证时允许手动输入年龄
      if (this.isMainlandId) return

      const s = String(val || '').trim()
      if (s === '') {
        this.$emit('input', { ...this.value, age: '' })
        return
      }

      // 只允许 0-120 的整数
      if (!/^\d{1,3}$/.test(s)) return
      const n = parseInt(s, 10)
      if (n < 0 || n > 120) return

      this.$emit('input', { ...this.value, age: n })
      // 手动输入年龄后，重新计算票种
      this.computeTicketTypeAndMaybePrompt(this.value.platform, this.value.area, n)
    },
    onIdNoInput(val) {
      // 先更新证件号
      const next = { ...this.value, idNo: val }

      // 仅对 18 位身份证自动计算年龄；其他证件则允许手动输入年龄
      const birth = parseBirthDateFromIdCard(val)
      if (birth) {
        const age = calcAge(birth)
        next.age = age
        // 证件解析后，计算票种并可能触发免票提示
        this.$emit('input', next)
        this.$nextTick(() => {
          this.computeTicketTypeAndMaybePrompt(next.platform, next.area, age)
        })
        return
      } else {
        // 切换为“非大陆证件”时，如果当前 age 还是提示文案，则清空为可输入状态
        if (String(next.age || '') === '系统将自动计算') {
          next.age = ''
        }
      }

      this.$emit('input', next)
    }
  }
}
</script>

<style scoped>
.block {
  padding: 10px 12px 4px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

.sub-title {
  padding: 8px 0 10px;
  color: #409eff;
  font-weight: 700;
  border-bottom: 1px dashed #e4e7ed;
  margin-bottom: 12px;
}

.block-actions {
  padding-top: 4px;
}
</style>

